/**
 * AdminAudit — operator view of destructive-endpoint activity.
 *
 * Backed by:
 *   GET /api/admin/audit?severity=&endpoint=&user_id=
 *   GET /api/admin/safety-status
 *
 * Pure-read page. The system never offers "Clear audit log" — by
 * design, the audit trail is append-only.
 */
import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, Badge } from "../../components/ui-extras";
import { ArrowsClockwise, ShieldCheck, ShieldWarning, FloppyDisk, Envelope, DownloadSimple } from "@phosphor-icons/react";

const SEVERITY_VARIANT = {
  CRITICAL: "error",
  WARN: "warning",
  WARNING: "warning",
  INFO: "info",
};

const SEVERITIES = ["", "CRITICAL", "WARN", "INFO"];

export default function AdminAudit() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [severity, setSeverity] = useState("");
  const [endpoint, setEndpoint] = useState("");
  const [drill, setDrill] = useState(null);
  const [backupsManifest, setBackupsManifest] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (severity) params.severity = severity;
      if (endpoint) params.endpoint = endpoint;
      const [a, s, m] = await Promise.all([
        api.get("/admin/audit", { params }),
        api.get("/admin/safety-status"),
        api.get("/admin/backups/final-manifest").catch(() => ({ data: null })),
      ]);
      setRows(a.data.audit || []);
      setStatus(s.data);
      setBackupsManifest(m?.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load audit");
    } finally { setLoading(false); }
  };

  const downloadBackup = async (kind) => {
    const paths = {
      mongo: "/admin/backups/final-mongo",
      objectstore: "/admin/backups/final-objectstore",
    };
    const filenames = {
      mongo: "GETFUELCARD_FINAL_CUTOVER_MONGO_BACKUP_20260925.archive.gz",
      objectstore: "GETFUELCARD_FINAL_CUTOVER_OBJECTSTORE_BUNDLE_20260925.tar.gz",
    };
    setDownloading(kind);
    try {
      const resp = await api.get(paths[kind], { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filenames[kind];
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${filenames[kind]} downloaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to download ${kind} backup`);
    } finally {
      setDownloading(null);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [severity, endpoint]);

  return (
    <div className="space-y-6" data-testid="admin-audit-page">
      <PageHeader
        title="Admin Audit"
        subtitle="Every destructive endpoint call — executed, blocked, or rate-limited — landed here. This log is append-only."
        actions={
          <button className="gf-btn gf-btn-outline" onClick={load} data-testid="refresh-audit">
            <ArrowsClockwise size={14}/> Refresh
          </button>
        }
      />

      {/* Final cutover backup downloads — temporary, admin-only */}
      {backupsManifest && (
        <div className="gf-card gf-card-body" data-testid="final-cutover-backups">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="font-bold text-zinc-900">Final Cutover Backups (2026-09-25)</div>
              <div className="text-xs text-zinc-500 mt-1">
                Temporary Admin-only download of the pre-Publish backup pair. SHA-256 verified before streaming.
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                className="gf-btn gf-btn-primary"
                disabled={!backupsManifest?.mongo?.present || downloading === "mongo"}
                onClick={() => downloadBackup("mongo")}
                data-testid="download-final-mongo-backup"
                title={backupsManifest?.mongo?.expected_sha256}
              >
                <DownloadSimple size={14} />
                {downloading === "mongo" ? "Downloading…" : "Download Final Mongo Backup"}
              </button>
              <button
                className="gf-btn gf-btn-outline"
                disabled={!backupsManifest?.objectstore?.present || downloading === "objectstore"}
                onClick={() => downloadBackup("objectstore")}
                data-testid="download-final-objectstore-backup"
                title={backupsManifest?.objectstore?.expected_sha256}
              >
                <DownloadSimple size={14} />
                {downloading === "objectstore" ? "Downloading…" : "Download Final Object Store Backup"}
              </button>
            </div>
          </div>
          <div className="text-xs text-zinc-500 font-mono mt-3 space-y-1">
            {backupsManifest?.mongo && (
              <div data-testid="final-mongo-meta">
                mongo · {backupsManifest.mongo.filename} · {backupsManifest.mongo.size_bytes ? (backupsManifest.mongo.size_bytes/1024/1024).toFixed(2) + " MB" : "missing"} · sha256={backupsManifest.mongo.expected_sha256}
              </div>
            )}
            {backupsManifest?.objectstore && (
              <div data-testid="final-objectstore-meta">
                objectstore · {backupsManifest.objectstore.filename} · {backupsManifest.objectstore.size_bytes ? (backupsManifest.objectstore.size_bytes/1024) .toFixed(2) + " KB" : "missing"} · sha256={backupsManifest.objectstore.expected_sha256}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Safety status pulse */}
      {status && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="safety-status-cards">
          <div className="gf-card gf-card-body" data-testid="status-kill-switch">
            <div className="flex items-center gap-2">
              {status.destructive_allowed ? (
                <><ShieldWarning size={18} className="text-amber-600"/><div>
                  <div className="font-bold text-amber-700">Destructive endpoints ENABLED</div>
                  <div className="text-xs text-zinc-500">Set <code>ALLOW_DESTRUCTIVE_ENDPOINTS=false</code> to lock.</div>
                </div></>
              ) : (
                <><ShieldCheck size={18} className="text-emerald-600"/><div>
                  <div className="font-bold text-emerald-700">Destructive endpoints LOCKED</div>
                  <div className="text-xs text-zinc-500">Kill-switch is closed. All bulk deletes return 403.</div>
                </div></>
              )}
            </div>
            <div className="text-xs text-zinc-400 mt-2">env={status.environment}</div>
          </div>

          <div className="gf-card gf-card-body" data-testid="status-critical-24h">
            <div className="overline">CRITICAL ACTIONS · LAST 24H</div>
            <div className="text-3xl font-mono mt-1">{status.critical_actions_last_24h}</div>
            <div className="text-xs text-zinc-500 mt-1">Includes reset-all + bulk-delete &gt;100 rows.</div>
          </div>

          <div className="gf-card gf-card-body" data-testid="status-backup">
            <div className="overline flex items-center gap-1"><FloppyDisk size={12}/> NIGHTLY MONGO BACKUP</div>
            <div className="mt-1">
              <Badge variant={status.backup?.status === "ok" ? "success" : (status.backup?.status === "failed" ? "error" : "warning")}>
                {status.backup?.status || "unknown"}
              </Badge>
            </div>
            <div className="text-xs text-zinc-500 mt-1 font-mono">
              {status.backup?.last_ts || "no run yet"}
              {status.backup?.last_size_bytes ? ` · ${(status.backup.last_size_bytes / 1024 / 1024).toFixed(1)} MB` : ""}
            </div>
          </div>

          <div className="gf-card gf-card-body" data-testid="status-email-transport">
            <div className="overline flex items-center gap-1"><Envelope size={12}/> EMAIL TRANSPORT</div>
            <div className="mt-1">
              {status.email?.transport === "live" ? (
                <Badge variant={status.email?.env_environment === "production" ? "success" : "error"}>
                  LIVE — real Mailgun sends
                </Badge>
              ) : (
                <Badge variant="warning">MOCK — no outbound delivery</Badge>
              )}
            </div>
            <div className="text-xs text-zinc-500 mt-1 font-mono">
              env={status.email?.env_environment} · EMAIL_TRANSPORT={status.email?.env_email_transport}
              {" · "}AUTO={status.email?.auto_email_enabled ? "on" : "off"}
              {status.email?.env_email_mock_mode?.toLowerCase?.() === "true" && (
                <span className="ml-1 text-amber-700 font-bold">· EMAIL_MOCK_MODE=true (kill-switch)</span>
              )}
              {status.email?.env_email_send_enabled?.toLowerCase?.() === "false" && (
                <span className="ml-1 text-amber-700 font-bold">· EMAIL_SEND_ENABLED=false (kill-switch)</span>
              )}
            </div>
            {status.email?.transport === "live" && status.email?.env_environment !== "production" && (
              <div className="text-xs text-red-600 mt-1 font-bold">
                ⚠ Outbound delivery is ENABLED outside production. Set EMAIL_TRANSPORT=mock to safe-mode.
              </div>
            )}
          </div>
        </div>
      )}

      <div className="gf-card gf-card-body flex flex-wrap gap-3 items-end">
        <div>
          <div className="overline mb-1">SEVERITY</div>
          <select className="gf-input" value={severity} onChange={(e) => setSeverity(e.target.value)} data-testid="filter-audit-severity">
            {SEVERITIES.map((s) => <option key={s} value={s}>{s || "All"}</option>)}
          </select>
        </div>
        <div>
          <div className="overline mb-1">ENDPOINT</div>
          <input className="gf-input" placeholder="e.g. transactions:reset_all" value={endpoint} onChange={(e) => setEndpoint(e.target.value)} data-testid="filter-audit-endpoint"/>
        </div>
        <div className="text-xs text-zinc-500">Showing {rows.length} row(s).</div>
      </div>

      <div className="gf-card">
        <div className="overflow-auto">
          <table className="gf-table" data-testid="audit-table">
            <thead>
              <tr>
                <th>WHEN</th>
                <th>SEVERITY</th>
                <th>ENDPOINT</th>
                <th>ACTION</th>
                <th className="text-right">DELETED</th>
                <th>USER</th>
                <th>IP</th>
                <th>ENV</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="text-center text-zinc-400 py-8">Loading…</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={9} className="text-center text-zinc-400 py-8">No audit entries match the current filters.</td></tr>
              )}
              {rows.map((r, i) => (
                <tr key={i} data-testid={`audit-row-${i}`}>
                  <td className="font-mono text-xs">{r.created_at?.slice(0, 19).replace("T", " ")}</td>
                  <td><Badge variant={SEVERITY_VARIANT[r.severity] || "info"}>{r.severity}</Badge></td>
                  <td className="font-mono text-xs">{r.endpoint}</td>
                  <td className="text-xs">{r.action}</td>
                  <td className="gf-num">{r.deleted_count ?? 0}</td>
                  <td className="text-xs text-zinc-600">{r.user_email || r.user_id?.slice(-6) || "—"}</td>
                  <td className="font-mono text-xs text-zinc-500">{r.ip || "—"}</td>
                  <td className="text-xs text-zinc-500">{r.environment}</td>
                  <td className="text-right">
                    <button className="gf-btn gf-btn-ghost" onClick={() => setDrill(r)} data-testid={`audit-drill-${i}`}>
                      Inspect
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {drill && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setDrill(null)}>
          <div className="bg-white w-full max-w-3xl border border-zinc-200 max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="audit-drill-modal">
            <div className="gf-card-header">
              <h3>Audit · {drill.endpoint} · {drill.action}</h3>
              <div className="text-xs text-zinc-500 mt-1 font-mono">{drill.created_at}</div>
            </div>
            <div className="overflow-auto flex-1 p-4 text-xs">
              <pre className="bg-zinc-50 border border-zinc-200 p-3 overflow-x-auto whitespace-pre-wrap">{JSON.stringify(drill, null, 2)}</pre>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
              <button className="gf-btn gf-btn-outline" onClick={() => setDrill(null)} data-testid="close-audit-drill">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
